/**
 * Folds a stream of live-STT events into the text on screen. Pure — no I/O.
 *
 * Streaming recognizers emit a fast, revisable guess for the segment being
 * spoken (`interim`) and then commit it (`final`). Interims REPLACE the pending
 * tail; finals APPEND to the committed prefix and clear the tail. That is the
 * whole "text jitters, then settles" behavior of live captions. A final marked
 * `replacesLast` is the recognizer re-issuing the segment it just committed
 * (e.g. a formatted copy of an unformatted end-of-turn) — it swaps the last
 * committed segment instead of appending a duplicate.
 */

export type TranscriptEvent =
  | { kind: "interim"; text: string }
  | { kind: "final"; text: string; replacesLast?: boolean };

export interface LiveTranscript {
  committed: string[];
  pending: string;
}

export const EMPTY_LIVE_TRANSCRIPT: LiveTranscript = {
  committed: [],
  pending: "",
};

export function applyTranscriptEvent(
  state: LiveTranscript,
  event: TranscriptEvent,
): LiveTranscript {
  const text = event.text.trim();
  if (event.kind === "interim") return { ...state, pending: text };
  // An empty revision carries nothing to replace with — keep what we have.
  if (event.replacesLast && !text) return { ...state, pending: "" };
  const base =
    event.replacesLast && state.committed.length > 0
      ? state.committed.slice(0, -1)
      : state.committed;
  return { committed: text ? [...base, text] : base, pending: "" };
}

/** The committed segments plus the still-revisable tail, as one string. */
export function liveTranscriptText(state: LiveTranscript): string {
  return [...state.committed, state.pending].filter(Boolean).join(" ");
}

/** Only what the recognizer has committed — what gets stored as the draft. */
export function committedTranscriptText(state: LiveTranscript): string {
  return state.committed.join(" ");
}
